<template>
    <div id="page_subspace_manage" class="ssm">
        <div class="ssm-main-layout">
            <header class="ssm-project-name-bar">
                <span class="ssm-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <div class="ssm-project-name-main">
                    <h1 class="ssm-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
                    <div class="ssm-project-meta">
                        <span class="ssm-project-type-tag" :class="spaceMode">{{ projectTypeLabel }}</span>
                        <span class="ssm-project-type-hint">{{ projectTypeHint }}</span>
                    </div>
                </div>
            </header>

            <div v-if="!isSupportedProject" class="ssm-panel">
                <div class="ssm-unsupported">
                    <el-empty description="当前项目类型暂不支持子空间管理（仅研伴项目、结对项目可用）" />
                </div>
            </div>

            <div v-else class="ssm-panel">
                <div class="ssm-panel-header">
                    <div class="ssm-panel-title">子空间管理</div>
                    <div class="ssm-panel-actions">
                        <button type="button" class="ssm-btn ssm-btn-primary ssm-btn-sm" @click="openImportDialog">
                            批量导入
                        </button>
                        <button type="button" class="ssm-btn ssm-btn-primary ssm-btn-sm" @click="openCreateDialog">
                            {{ createBtnText }}
                        </button>
                    </div>
                </div>

                <el-form :inline="true" :model="filters" class="ssm-filter-bar" @submit.native.prevent>
                    <div class="ssm-filter-left">
                        <el-form-item>
                            <el-select v-model="filters.school" size="small" clearable filterable placeholder="全部校级空间"
                                style="width: 240px;">
                                <el-option label="全部校级空间" value="" />
                                <el-option v-for="s in schoolSpaceOptions" :key="s.id" :label="s.name" :value="s.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="filters.origin" size="small" clearable placeholder="全部创建来源"
                                style="width: 156px;">
                                <el-option label="全部创建来源" value="" />
                                <el-option label="项目管理员创建" value="project" />
                                <el-option label="校级管理员创建" value="school" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="filters.status" size="small" clearable placeholder="全部状态"
                                style="width: 120px;">
                                <el-option label="全部状态" value="" />
                                <el-option label="进行中" value="active" />
                                <el-option label="已归档" value="archived" />
                            </el-select>
                        </el-form-item>
                    </div>
                    <el-form-item class="ssm-filter-search">
                        <el-input v-model.trim="filters.search" size="small" clearable :placeholder="searchPlaceholder"
                            suffix-icon="el-icon-search" style="width: 200px;" />
                    </el-form-item>
                </el-form>

                <div class="ssm-panel-body">
                    <el-table :data="pagedRows" stripe style="width: 100%">
                        <el-table-column type="index" label="序号" width="80" align="center" :index="indexMethod" />
                        <el-table-column prop="name" label="空间名称" min-width="160" show-overflow-tooltip>
                            <template slot-scope="{ row }">
                                <span class="ssm-cell-title">{{ row.name }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="创建来源" width="120" align="center">
                            <template slot-scope="{ row }">
                                <span class="ssm-origin-tag" :class="row.origin">
                                    {{ row.origin === 'school' ? '校级管理员' : '项目管理员' }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="schoolSpaceName" label="校级空间" min-width="200" show-overflow-tooltip>
                            <template slot-scope="{ row }">
                                {{ row.schoolSpaceName || '—' }}
                            </template>
                        </el-table-column>
                        <el-table-column v-if="isPairMode" label="班级" min-width="200">
                            <template slot-scope="{ row }">
                                <div v-if="row.classes && row.classes.length" class="ssm-cell-classes">
                                    <div v-for="(c, ci) in row.classes" :key="ci" class="ssm-class-line">{{ c }}</div>
                                </div>
                                <span v-else>—</span>
                            </template>
                        </el-table-column>
                        <el-table-column v-else prop="leader" label="学科负责人" min-width="120" show-overflow-tooltip>
                            <template slot-scope="{ row }">
                                {{ row.leader || '—' }}
                            </template>
                        </el-table-column>
                        <el-table-column v-if="isPairMode" prop="teachers" label="指导教师" min-width="140"
                            show-overflow-tooltip>
                            <template slot-scope="{ row }">
                                {{ row.teachers || '—' }}
                            </template>
                        </el-table-column>
                        <el-table-column v-else label="成员" min-width="140">
                            <template slot-scope="{ row }">
                                {{ memberText(row) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="createdAt" label="创建时间" width="160" align="center"
                            show-overflow-tooltip />
                        <el-table-column label="操作" width="160" align="center"
                            :fixed="pagedRows.length ? 'right' : false">
                            <template slot-scope="{ row }">
                                <el-button type="text" size="small" @click="openEditDialog(row)">
                                    {{ row.status === 'archived' ? '查看' : '编辑' }}
                                </el-button>
                                <el-button type="text" size="small" class="ssm-delete-btn" @click="onDelete(row)">
                                    删除
                                </el-button>
                            </template>
                        </el-table-column>
                        <template slot="empty">
                            <el-empty :description="emptyDesc" />
                        </template>
                    </el-table>
                </div>
                <pagination :total="filteredRows.length" :page.sync="pages.currentPage" :limit.sync="pages.pageSize"
                    :pageSizes="[10, 20, 30]" :hide-on-single-page="true" @pagination="handlePageChange" />
            </div>
        </div>

        <!-- 创建 / 编辑 -->
        <el-dialog :title="formDialogTitle" :visible.sync="formDialogVisible" width="640px" append-to-body
            custom-class="ssm-form-dialog" destroy-on-close @closed="resetForm">
            <el-form class="ssm-form" label-position="top" @submit.native.prevent>
                <el-form-item required>
                    <template slot="label">校级空间 <span class="ssm-required">*</span></template>
                    <p class="ssm-form-hint">{{ schoolSpaceHint }}</p>
                    <el-select v-model="form.schoolSpaceId" placeholder="请选择校级空间" filterable style="width:100%;"
                        :disabled="formReadonly">
                        <el-option v-for="s in schoolSpaceOptions" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item required>
                    <template slot="label">空间名称 <span class="ssm-required">*</span></template>
                    <el-input v-model.trim="form.name" :placeholder="namePlaceholder" maxlength="50" clearable
                        :disabled="formReadonly" />
                </el-form-item>

                <template v-if="isPairMode">
                    <el-form-item required>
                        <template slot="label">结对学校与班级 <span class="ssm-required">*</span></template>
                        <p class="ssm-form-hint">请至少选择 2 所学校的班级。</p>
                        <el-checkbox-group v-model="form.classes" class="ssm-check-grid" :disabled="formReadonly">
                            <label v-for="item in pairClassOptions" :key="item" class="ssm-check-item">
                                <el-checkbox :label="item">{{ item }}</el-checkbox>
                            </label>
                        </el-checkbox-group>
                    </el-form-item>
                    <el-form-item label="指导教师">
                        <el-input v-model.trim="form.teachers" placeholder="如：张老师、李老师" clearable
                            :disabled="formReadonly" />
                    </el-form-item>
                </template>

                <template v-else>
                    <el-form-item label="学科负责人">
                        <el-select v-model="form.leader" placeholder="请选择学科负责人" clearable style="width:100%;"
                            :disabled="formReadonly">
                            <el-option v-for="item in leaderOptions" :key="item" :label="item" :value="item" />
                        </el-select>
                    </el-form-item>
                    <el-form-item required>
                        <template slot="label">师傅（可多选） <span class="ssm-required">*</span></template>
                        <el-checkbox-group v-model="form.mentors" class="ssm-check-grid" :disabled="formReadonly">
                            <label v-for="item in mentorOptions" :key="'m-' + item" class="ssm-check-item">
                                <el-checkbox :label="item">{{ item }}</el-checkbox>
                            </label>
                        </el-checkbox-group>
                    </el-form-item>
                    <el-form-item required>
                        <template slot="label">徒弟（可多选） <span class="ssm-required">*</span></template>
                        <el-checkbox-group v-model="form.mentees" class="ssm-check-grid" :disabled="formReadonly">
                            <label v-for="item in menteeOptions" :key="'e-' + item" class="ssm-check-item">
                                <el-checkbox :label="item">{{ item }}</el-checkbox>
                            </label>
                        </el-checkbox-group>
                    </el-form-item>
                </template>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="formDialogVisible = false">{{ formReadonly ? '关 闭' : '取 消' }}</el-button>
                <el-button v-if="!formReadonly" type="primary" :loading="formSaving" @click="submitForm">
                    {{ editMode ? '保存修改' : '确定创建' }}
                </el-button>
            </span>
        </el-dialog>

        <!-- 批量导入 -->
        <el-dialog title="批量导入子空间" :visible.sync="importDialogVisible" width="640px" append-to-body
            custom-class="ssm-import-dialog" destroy-on-close @closed="resetImport">
            <div class="ssm-import-steps">
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">1</span>下载导入模板
                    </div>
                    <p class="ssm-import-desc">{{ importDesc }}</p>
                    <ul class="ssm-import-tpl-fields">
                        <li v-html="importFieldsHtml" />
                    </ul>
                    <el-button size="small" icon="el-icon-download" style="margin-top:10px;"
                        @click="downloadTemplate">下载导入模板（.csv）</el-button>
                </div>
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">2</span>上传填写好的文件
                    </div>
                    <p class="ssm-import-desc">支持 .csv（推荐使用本页下载的模板，可用 Excel 打开编辑后另存为 CSV）。</p>
                    <div class="ssm-upload-zone" :class="{ dragover: importDragover }" tabindex="0" role="button"
                        @click="$refs.importFile.click()" @keydown.enter.prevent="$refs.importFile.click()"
                        @dragenter.prevent="importDragover = true" @dragover.prevent="importDragover = true"
                        @dragleave.prevent="importDragover = false" @drop.prevent="onImportDrop">
                        <div class="ssm-uz-icon">📄</div>
                        <div class="ssm-uz-title">点击选择文件，或拖拽到此处</div>
                        <div class="ssm-uz-hint">单次建议不超过 200 行</div>
                        <div v-if="importFileName" class="ssm-uz-file">已选择：{{ importFileName }}</div>
                    </div>
                    <input ref="importFile" type="file" accept=".csv,.txt,text/csv" hidden @change="onImportFileChange">
                </div>
                <div class="ssm-import-step">
                    <div class="ssm-import-step-title">
                        <span class="ssm-import-step-num">3</span>校验预览
                    </div>
                    <div v-if="importRows.length" class="ssm-import-summary">
                        <span class="ssm-sum-item">共 {{ importRows.length }} 行</span>
                        <span class="ssm-sum-item ok">有效 {{ importOkCount }}</span>
                        <span class="ssm-sum-item err">无效 {{ importRows.length - importOkCount }}</span>
                    </div>
                    <div class="ssm-import-preview-wrap">
                        <div v-if="!importRows.length" class="ssm-import-empty">上传文件后将在此预览校验结果</div>
                        <table v-else class="ssm-import-preview">
                            <thead>
                                <tr>
                                    <th style="width:44px;">行</th>
                                    <th style="width:72px;">结果</th>
                                    <th style="width:72px;">类型</th>
                                    <th>空间名称</th>
                                    <th>关键信息</th>
                                    <th>说明</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="r in importRows" :key="r.lineNo" :class="{ 'is-invalid': !r.valid }">
                                    <td>{{ r.lineNo }}</td>
                                    <td :class="r.valid ? 'row-ok' : 'row-err'">{{ r.valid ? '通过' : '失败' }}</td>
                                    <td>{{ r.type === 'pair' ? '结对班级' : '研伴空间' }}</td>
                                    <td>{{ r.name || '—' }}</td>
                                    <td>{{ r.info || '—' }}</td>
                                    <td>{{ r.message }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="importDialogVisible = false">取 消</el-button>
                <el-button type="primary" :disabled="importOkCount === 0" :loading="importSaving"
                    @click="confirmImport">确认导入</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import pagination from '@/common/community/pagination.vue';
    import {
        mapState
    } from 'vuex';
    import {
        saveAs
    } from 'file-saver';

    /** modelType：4 研伴项目，5 结对项目 */
    const MODEL_TYPE = {
        YANBAN: 4,
        PAIR: 5,
    };

    const PROJECT_META = {
        pair: {
            label: '结对项目',
            hint: '本项目仅可创建班级空间',
            createBtn: '＋ 创建班级空间',
            createTitle: '创建班级空间',
            editTitle: '编辑班级空间',
            schoolHint: '请选择本子空间所属的校级结对空间。',
            namePlaceholder: '如：初一（2）班结对空间',
            searchPlaceholder: '搜索空间名称、班级、指导教师…',
            importDesc: '当前为结对项目，仅可导入班级空间。请下载模板填写后上传。',
            importFieldsHtml: '<strong>班级空间：</strong>填写空间类型、空间名称、结对班级（≥2 个，用中文分号分隔）、指导教师（可选）',
        },
        yanban: {
            label: '研伴项目',
            hint: '本项目仅可创建研伴空间',
            createBtn: '＋ 创建研伴空间',
            createTitle: '创建研伴空间',
            editTitle: '编辑研伴空间',
            schoolHint: '请选择本子空间所属的校级研伴空间。',
            namePlaceholder: '如：曹靖人 · 李明研伴空间',
            searchPlaceholder: '搜索空间名称、成员、学科负责人…',
            importDesc: '当前为研伴项目，仅可导入研伴空间。请下载模板填写后上传。',
            importFieldsHtml: '<strong>研伴空间：</strong>填写空间类型、空间名称、师傅、徒弟（必填，多人用中文分号分隔）；学科负责人可选',
        },
    };

    const SCHOOL_SPACES = {
        pair: [{
            id: 'pair-sy',
            name: '首都师范大学附属顺义实验中学 · 校级结对空间',
        },
        {
            id: 'pair-nmg',
            name: '内蒙古扎赉特旗音德尔第六中学 · 校级结对空间',
        },
        {
            id: 'pair-cd',
            name: '河北承德实验中学 · 校级结对空间',
        },
        ],
        yanban: [{
            id: 'yanban-sy',
            name: '首都师范大学附属顺义实验中学 · 校级研伴空间',
        },
        {
            id: 'yanban-nmg',
            name: '内蒙古扎赉特旗音德尔第六中学 · 校级研伴空间',
        },
        {
            id: 'yanban-cd',
            name: '河北承德实验中学 · 校级研伴空间',
        },
        ],
    };

    const PAIR_CLASS_OPTIONS = [
        '首师大附中顺义 · 初一（2）班',
        '对口协作校 A · 初一（2）班',
        '首师大附中顺义 · 初三（1）班',
        '对口协作校 B · 初三（1）班',
        '首师大附中顺义 · 初一（5）班',
        '对口协作校 A · 初一（5）班',
    ];

    const LEADER_OPTIONS = ['丁大', '曹靖人', '赵老师'];
    const MENTOR_OPTIONS = ['曹靖人', '丁大', '张老师', '李老师'];
    const MENTEE_OPTIONS = ['李明', '王芳', '陈晨', '刘洋'];

    const nowStr = () => {
        const d = new Date();
        const z = (n) => (n < 10 ? `0${n}` : `${n}`);
        return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}`;
    };

    const splitMulti = (val) => String(val || '')
        .split(/[;；,，|/]/)
        .map((s) => s.trim())
        .filter(Boolean);

    const escapeCsv = (value) => {
        const str = value == null ? '' : String(value);
        if (/[",\n\r]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    let idSeed = 100;

    export default {
        name: 'SubspaceManage',
        components: {
            pagination,
        },
        data() {
            return {
                list: [],
                filters: {
                    school: '',
                    origin: '',
                    status: '',
                    search: '',
                },
                pages: {
                    currentPage: 1,
                    pageSize: 10,
                },
                formDialogVisible: false,
                formSaving: false,
                editMode: false,
                formReadonly: false,
                editingId: null,
                form: {
                    schoolSpaceId: '',
                    name: '',
                    classes: [],
                    teachers: '',
                    leader: '',
                    mentors: [],
                    mentees: [],
                },
                pairClassOptions: PAIR_CLASS_OPTIONS,
                leaderOptions: LEADER_OPTIONS,
                mentorOptions: MENTOR_OPTIONS,
                menteeOptions: MENTEE_OPTIONS,
                importDialogVisible: false,
                importDragover: false,
                importFileName: '',
                importRows: [],
                importSaving: false,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            modelType() {
                const t = this.currentProject && this.currentProject.modelType;
                return t == null || t === '' ? null : Number(t);
            },
            spaceMode() {
                if (this.modelType === MODEL_TYPE.YANBAN) return 'yanban';
                if (this.modelType === MODEL_TYPE.PAIR) return 'pair';
                return '';
            },
            isSupportedProject() {
                return this.spaceMode === 'pair' || this.spaceMode === 'yanban';
            },
            isPairMode() {
                return this.spaceMode === 'pair';
            },
            meta() {
                return PROJECT_META[this.spaceMode] || PROJECT_META.pair;
            },
            projectTypeLabel() {
                if (!this.isSupportedProject) {
                    const map = {
                        1: '教师类',
                        2: '学生类',
                        3: '其他',
                        4: '研伴项目',
                        5: '结对项目',
                        6: '三交活动',
                        7: '大赛活动',
                        8: '三段九环',
                    };
                    return map[this.modelType] || '未知类型';
                }
                return this.meta.label;
            },
            projectTypeHint() {
                if (!this.isSupportedProject) return '子空间管理仅支持研伴项目与结对项目';
                return this.meta.hint;
            },
            createBtnText() {
                return this.meta.createBtn;
            },
            schoolSpaceHint() {
                return this.meta.schoolHint;
            },
            namePlaceholder() {
                return this.meta.namePlaceholder;
            },
            searchPlaceholder() {
                return this.meta.searchPlaceholder;
            },
            importDesc() {
                return this.meta.importDesc;
            },
            importFieldsHtml() {
                return this.meta.importFieldsHtml;
            },
            schoolSpaceOptions() {
                return SCHOOL_SPACES[this.spaceMode] || [];
            },
            formDialogTitle() {
                if (this.formReadonly) {
                    return this.isPairMode ? '查看班级空间' : '查看研伴空间';
                }
                return this.editMode ? this.meta.editTitle : this.meta.createTitle;
            },
            emptyDesc() {
                return this.isPairMode ? '暂无班级空间，请点击「创建班级空间」新建' : '暂无研伴空间，请点击「创建研伴空间」新建';
            },
            filteredRows() {
                const q = (this.filters.search || '').trim().toLowerCase();
                return this.list.filter((row) => {
                    if (row.type !== this.spaceMode) return false;
                    if (this.filters.school && row.schoolSpaceId !== this.filters.school) return false;
                    if (this.filters.origin && row.origin !== this.filters.origin) return false;
                    if (this.filters.status && row.status !== this.filters.status) return false;
                    if (q) {
                        const kw = [
                            row.name,
                            row.teachers,
                            row.leader,
                            row.schoolSpaceName,
                            ...(row.classes || []),
                            ...(row.mentors || []),
                            ...(row.mentees || []),
                        ].join(' ').toLowerCase();
                        if (!kw.includes(q)) return false;
                    }
                    return true;
                });
            },
            pagedRows() {
                const start = (this.pages.currentPage - 1) * this.pages.pageSize;
                return this.filteredRows.slice(start, start + this.pages.pageSize);
            },
            importOkCount() {
                return this.importRows.filter((r) => r.valid).length;
            },
        },
        watch: {
            'currentProject.id'() {
                this.pages.currentPage = 1;
                this.resetFilters();
                this.initMockList();
            },
            'currentProject.modelType'() {
                this.pages.currentPage = 1;
                this.resetFilters();
                this.initMockList();
            },
            'filters.school'() {
                this.pages.currentPage = 1;
            },
            'filters.origin'() {
                this.pages.currentPage = 1;
            },
            'filters.status'() {
                this.pages.currentPage = 1;
            },
            'filters.search'() {
                this.pages.currentPage = 1;
            },
        },
        mounted() {
            this.initMockList();
        },
        methods: {
            resetFilters() {
                this.filters = {
                    school: '',
                    origin: '',
                    status: '',
                    search: '',
                };
            },
            memberText(row) {
                const mentors = (row.mentors || []).length;
                const mentees = (row.mentees || []).length;
                return `师傅 ${mentors} · 徒弟 ${mentees}`;
            },
            indexMethod(index) {
                return (this.pages.currentPage - 1) * this.pages.pageSize + index + 1;
            },
            handlePageChange(data) {
                this.pages.currentPage = data.pageIndex;
                this.pages.pageSize = data.pageSize;
            },
            getSchoolName(id) {
                const all = SCHOOL_SPACES.pair.concat(SCHOOL_SPACES.yanban);
                const hit = all.find((s) => s.id === id);
                return hit ? hit.name : '';
            },
            // 暂无后端接口，先用与原型一致的本地演示数据
            initMockList() {
                if (!this.isSupportedProject) {
                    this.list = [];
                    return;
                }
                if (this.isPairMode) {
                    this.list = [{
                        id: 'p1',
                        type: 'pair',
                        status: 'active',
                        origin: 'project',
                        schoolSpaceId: 'pair-sy',
                        schoolSpaceName: this.getSchoolName('pair-sy'),
                        name: '初一（2）班结对空间',
                        classes: ['首师大附中顺义 · 初一（2）班', '对口协作校 A · 初一（2）班'],
                        teachers: '张老师、李老师',
                        createdAt: '2026-05-18 14:20',
                    },
                    {
                        id: 'p2',
                        type: 'pair',
                        status: 'active',
                        origin: 'school',
                        schoolSpaceId: 'pair-sy',
                        schoolSpaceName: this.getSchoolName('pair-sy'),
                        name: '初一（5）班结对空间',
                        classes: ['首师大附中顺义 · 初一（5）班', '对口协作校 A · 初一（5）班'],
                        teachers: '刘老师',
                        createdAt: '2026-05-20 10:15',
                    },
                    {
                        id: 'p3',
                        type: 'pair',
                        status: 'active',
                        origin: 'project',
                        schoolSpaceId: 'pair-sy',
                        schoolSpaceName: this.getSchoolName('pair-sy'),
                        name: '初三（1）班结对空间',
                        classes: [
                            '首师大附中顺义 · 初三（1）班',
                            '对口协作校 A · 初三（1）班',
                            '对口协作校 B · 初三（1）班',
                        ],
                        teachers: '王老师',
                        createdAt: '2026-05-12 09:40',
                    },
                    {
                        id: 'p4',
                        type: 'pair',
                        status: 'archived',
                        origin: 'school',
                        schoolSpaceId: 'pair-nmg',
                        schoolSpaceName: this.getSchoolName('pair-nmg'),
                        name: '2025寒假 · 初二结对空间',
                        classes: ['首师大附中顺义 · 初二（1）班', '对口协作校 A · 初二（2）班'],
                        teachers: '赵老师',
                        createdAt: '2025-02-28 18:00',
                    },
                    ];
                } else {
                    this.list = [{
                        id: 'y1',
                        type: 'yanban',
                        status: 'active',
                        origin: 'project',
                        schoolSpaceId: 'yanban-sy',
                        schoolSpaceName: this.getSchoolName('yanban-sy'),
                        name: '曹靖人 · 李明研伴空间',
                        leader: '丁大',
                        mentors: ['曹靖人'],
                        mentees: ['李明'],
                        createdAt: '2026-05-16 11:05',
                    },
                    {
                        id: 'y2',
                        type: 'yanban',
                        status: 'active',
                        origin: 'school',
                        schoolSpaceId: 'yanban-sy',
                        schoolSpaceName: this.getSchoolName('yanban-sy'),
                        name: '张老师 · 陈晨研伴空间',
                        leader: '赵老师',
                        mentors: ['张老师'],
                        mentees: ['陈晨'],
                        createdAt: '2026-05-21 15:40',
                    },
                    {
                        id: 'y3',
                        type: 'yanban',
                        status: 'active',
                        origin: 'project',
                        schoolSpaceId: 'yanban-sy',
                        schoolSpaceName: this.getSchoolName('yanban-sy'),
                        name: '丁大 · 王芳研伴空间',
                        leader: '曹靖人',
                        mentors: ['丁大'],
                        mentees: ['王芳'],
                        createdAt: '2026-05-10 16:30',
                    },
                    ];
                }
            },
            resetForm() {
                this.editMode = false;
                this.formReadonly = false;
                this.editingId = null;
                this.formSaving = false;
                this.form = {
                    schoolSpaceId: '',
                    name: '',
                    classes: this.isPairMode ? PAIR_CLASS_OPTIONS.slice(0, 2) : [],
                    teachers: '',
                    leader: '',
                    mentors: [],
                    mentees: [],
                };
            },
            openCreateDialog() {
                this.resetForm();
                this.formDialogVisible = true;
            },
            openEditDialog(row) {
                this.editMode = true;
                this.formReadonly = row.status === 'archived';
                this.editingId = row.id;
                this.form = {
                    schoolSpaceId: row.schoolSpaceId || '',
                    name: row.name || '',
                    classes: [...(row.classes || [])],
                    teachers: row.teachers || '',
                    leader: row.leader || '',
                    mentors: [...(row.mentors || [])],
                    mentees: [...(row.mentees || [])],
                };
                this.formDialogVisible = true;
            },
            submitForm() {
                if (!this.form.schoolSpaceId) {
                    this.$message.warning('请选择校级空间');
                    return;
                }
                if (!this.form.name) {
                    this.$message.warning('请填写空间名称');
                    return;
                }
                if (this.isPairMode) {
                    if ((this.form.classes || []).length < 2) {
                        this.$message.warning('结对班级空间请至少选择 2 个班级');
                        return;
                    }
                } else {
                    if (!(this.form.mentors || []).length || !(this.form.mentees || []).length) {
                        this.$message.warning('请至少各选择 1 位师傅与徒弟');
                        return;
                    }
                }

                this.formSaving = true;
                const schoolSpaceName = this.getSchoolName(this.form.schoolSpaceId);
                if (this.editMode) {
                    const idx = this.list.findIndex((r) => r.id === this.editingId);
                    if (idx > -1) {
                        const prev = this.list[idx];
                        this.$set(this.list, idx, {
                            ...prev,
                            schoolSpaceId: this.form.schoolSpaceId,
                            schoolSpaceName,
                            name: this.form.name,
                            classes: [...(this.form.classes || [])],
                            teachers: this.form.teachers,
                            leader: this.form.leader,
                            mentors: [...(this.form.mentors || [])],
                            mentees: [...(this.form.mentees || [])],
                        });
                    }
                    this.formSaving = false;
                    this.formDialogVisible = false;
                    this.$message.success((this.isPairMode ? '班级空间' : '研伴空间') + '已保存');
                    return;
                }

                idSeed += 1;
                this.list.unshift({
                    id: `local-${idSeed}`,
                    type: this.spaceMode,
                    status: 'active',
                    origin: 'project',
                    schoolSpaceId: this.form.schoolSpaceId,
                    schoolSpaceName,
                    name: this.form.name,
                    classes: [...(this.form.classes || [])],
                    teachers: this.form.teachers,
                    leader: this.form.leader,
                    mentors: [...(this.form.mentors || [])],
                    mentees: [...(this.form.mentees || [])],
                    createdAt: nowStr(),
                });
                this.formSaving = false;
                this.formDialogVisible = false;
                this.$message.success((this.isPairMode ? '班级空间' : '研伴空间') + '创建成功');
            },
            onDelete(row) {
                this.$confirm(`确定删除「${row.name}」吗？删除后不可恢复。`, '提示', {
                    type: 'warning',
                    confirmButtonText: '删除',
                    cancelButtonText: '取消',
                }).then(() => {
                    this.list = this.list.filter((r) => r.id !== row.id);
                    this.$message.success('已删除');
                }).catch(() => { });
            },
            openImportDialog() {
                this.resetImport();
                this.importDialogVisible = true;
            },
            resetImport() {
                this.importDragover = false;
                this.importFileName = '';
                this.importRows = [];
                this.importSaving = false;
                if (this.$refs.importFile) {
                    this.$refs.importFile.value = '';
                }
            },
            downloadTemplate() {
                const headers = ['空间类型', '空间名称', '结对班级', '指导教师', '学科负责人', '师傅', '徒弟'];
                const samples = this.isPairMode ? [
                    ['结对班级', '初一（3）班结对空间', '首师大附中顺义·初一（3）班；对口协作校A·初一（3）班', '张老师；李老师', '', '', ''],
                    ['结对班级', '初二（1）班结对空间', '首师大附中顺义·初二（1）班；对口协作校B·初二（1）班', '王老师', '', '', ''],
                ] : [
                    ['研伴空间', '张老师 · 陈晨研伴空间', '', '', '丁大', '张老师', '陈晨'],
                    ['研伴空间', '李老师 · 刘洋研伴空间', '', '', '曹靖人', '李老师', '刘洋'],
                ];
                const lines = [headers, ...samples].map((row) => row.map(escapeCsv).join(','));
                const blob = new Blob(['\uFEFF' + lines.join('\n')], {
                    type: 'text/csv;charset=utf-8;',
                });
                const fileName = (this.isPairMode ? '班级空间' : '研伴空间') + '批量导入模板.csv';
                saveAs(blob, fileName);
                this.$message.success('模板已下载');
            },
            onImportDrop(e) {
                this.importDragover = false;
                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                if (file) this.readImportFile(file);
            },
            onImportFileChange(e) {
                const file = e.target.files && e.target.files[0];
                if (file) this.readImportFile(file);
            },
            readImportFile(file) {
                const lower = (file.name || '').toLowerCase();
                if (!/\.(csv|txt)$/i.test(lower) && !/csv|text\//.test(file.type || '')) {
                    this.$message.error('请上传 CSV 文件（可用 Excel 打开模板后另存为 CSV）');
                    return;
                }
                const reader = new FileReader();
                reader.onload = (ev) => {
                    this.handleImportText(ev.target.result || '', file.name);
                };
                reader.onerror = () => {
                    this.$message.error('文件读取失败');
                };
                reader.readAsText(file, 'UTF-8');
            },
            parseCsv(text) {
                const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
                const rows = [];
                lines.forEach((line) => {
                    if (!line.trim()) return;
                    const cols = [];
                    let cur = '';
                    let inQuote = false;
                    for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (inQuote) {
                            if (ch === '"') {
                                if (line[i + 1] === '"') {
                                    cur += '"';
                                    i++;
                                } else {
                                    inQuote = false;
                                }
                            } else {
                                cur += ch;
                            }
                        } else if (ch === '"') {
                            inQuote = true;
                        } else if (ch === ',' || ch === '\t') {
                            cols.push(cur);
                            cur = '';
                        } else {
                            cur += ch;
                        }
                    }
                    cols.push(cur);
                    rows.push(cols.map((c) => c.trim()));
                });
                return rows;
            },
            mapHeader(headers) {
                const map = {};
                headers.forEach((h, i) => {
                    const key = String(h || '').replace(/\s+/g, '');
                    if (/空间类型|类型/.test(key)) map.type = i;
                    else if (/空间名称|名称/.test(key)) map.name = i;
                    else if (/结对班级|班级/.test(key)) map.classes = i;
                    else if (/指导教师|教师/.test(key)) map.teachers = i;
                    else if (/学科负责人|负责人/.test(key)) map.leader = i;
                    else if (/师傅/.test(key)) map.mentors = i;
                    else if (/徒弟/.test(key)) map.mentees = i;
                });
                return map;
            },
            normalizeType(raw) {
                const t = String(raw || '').trim();
                if (/研伴/.test(t) || /yanban/i.test(t) || t === '一对一') return 'yanban';
                if (/结对/.test(t) || /班级/.test(t) || /pair/i.test(t)) return 'pair';
                return '';
            },
            validateImportRow(raw, lineNo) {
                const allowed = this.spaceMode;
                const type = this.normalizeType(raw.type) || allowed;
                const name = String(raw.name || '').trim();
                const errors = [];
                if (this.normalizeType(raw.type) && type !== allowed) {
                    errors.push(allowed === 'pair'
                        ? '结对项目仅允许导入班级空间（空间类型=结对班级）'
                        : '研伴项目仅允许导入研伴空间（空间类型=研伴空间）');
                }
                if (!name) errors.push('空间名称不能为空');

                const classes = splitMulti(raw.classes);
                const teachers = String(raw.teachers || '').trim();
                const leader = String(raw.leader || '').trim();
                const mentors = splitMulti(raw.mentors);
                const mentees = splitMulti(raw.mentees);
                let info = '';

                if (type === 'pair') {
                    if (classes.length < 2) errors.push('结对班级至少填写 2 个（用中文分号分隔）');
                    info = classes.join('；') + (teachers ? ` · 教师：${teachers}` : '');
                } else {
                    if (!mentors.length) errors.push('须至少填写 1 位师傅');
                    if (!mentees.length) errors.push('须至少填写 1 位徒弟');
                    info = `负责人：${leader || '—'} · 师傅：${mentors.join('、') || '—'} · 徒弟：${mentees.join('、') || '—'}`;
                }

                return {
                    lineNo,
                    valid: errors.length === 0,
                    type,
                    name,
                    classes,
                    teachers,
                    leader,
                    mentors,
                    mentees,
                    info,
                    message: errors.length ? errors.join('；') : '可导入',
                };
            },
            handleImportText(text, fileName) {
                const table = this.parseCsv(text);
                if (!table.length) {
                    this.$message.error('文件为空或无法解析');
                    this.resetImport();
                    return;
                }
                const map = this.mapHeader(table[0]);
                const start = (map.type != null || map.name != null) ? 1 : 0;
                if (map.type == null) map.type = 0;
                if (map.name == null) map.name = 1;
                if (map.classes == null) map.classes = 2;
                if (map.teachers == null) map.teachers = 3;
                if (map.leader == null) map.leader = 4;
                if (map.mentors == null) map.mentors = 5;
                if (map.mentees == null) map.mentees = 6;

                const rows = [];
                for (let i = start; i < table.length; i++) {
                    const cols = table[i].map((c) => (c == null ? '' : String(c).trim()));
                    if (!cols.join('').trim()) continue;
                    rows.push(this.validateImportRow({
                        type: cols[map.type] || '',
                        name: cols[map.name] || '',
                        classes: cols[map.classes] || '',
                        teachers: cols[map.teachers] || '',
                        leader: cols[map.leader] || '',
                        mentors: cols[map.mentors] || '',
                        mentees: cols[map.mentees] || '',
                    }, i + 1));
                }
                if (!rows.length) {
                    this.$message.warning('未识别到有效数据行');
                    this.resetImport();
                    return;
                }
                this.importFileName = `${fileName || '导入文件'}（${rows.length} 行）`;
                this.importRows = rows;
                const ok = rows.filter((r) => r.valid).length;
                this.$message[ok ? 'success' : 'warning'](`解析完成：有效 ${ok} / 共 ${rows.length}`);
            },
            confirmImport() {
                const valid = this.importRows.filter((r) => r.valid);
                if (!valid.length) {
                    this.$message.warning('没有可导入的有效数据');
                    return;
                }
                this.importSaving = true;
                const defaultSchool = this.schoolSpaceOptions[0];
                valid.slice().reverse().forEach((r) => {
                    idSeed += 1;
                    this.list.unshift({
                        id: `import-${idSeed}`,
                        type: this.spaceMode,
                        status: 'active',
                        origin: 'project',
                        schoolSpaceId: defaultSchool ? defaultSchool.id : '',
                        schoolSpaceName: defaultSchool ? defaultSchool.name : '',
                        name: r.name,
                        classes: r.classes || [],
                        teachers: r.teachers || '',
                        leader: r.leader || '',
                        mentors: r.mentors || [],
                        mentees: r.mentees || [],
                        createdAt: nowStr(),
                    });
                });
                this.importSaving = false;
                this.importDialogVisible = false;
                const skipped = this.importRows.length - valid.length;
                this.$message.success(`成功导入 ${valid.length} 个子空间${skipped ? `，跳过 ${skipped} 行无效数据` : ''}`);
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-light: #ECF5FF;
    @success: #67C23A;
    @success-bg: #F0F9EB;
    @danger: #F56C6C;
    @danger-bg: #FEF0F0;
    @purple: #8B5CF6;
    @purple-bg: #F5F3FF;
    @bg: #F5F7FA;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @text-placeholder: #C0C4CC;
    @border: #DCDFE6;
    @border-light: #EBEEF5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .ssm {
        min-height: 100%;
        // background: @bg;
    }

    .ssm-main-layout {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .ssm-project-name-bar {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .ssm-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .ssm-project-name-main {
        flex: 1;
        min-width: 0;
    }

    .ssm-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
    }

    .ssm-project-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    .ssm-project-type-tag {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 100px;
        font-size: 12px;
        font-weight: 600;
        background: @bg;
        color: @text-regular;

        &.pair {
            background: @primary-light;
            color: @primary;
        }

        &.yanban {
            background: @purple-bg;
            color: @purple;
        }
    }

    .ssm-project-type-hint {
        font-size: 12px;
        color: @text-secondary;
    }

    .ssm-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .ssm-unsupported {
        padding: 48px 16px;
    }

    .ssm-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 10px;
    }

    .ssm-panel-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .ssm-panel-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .ssm-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 14px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s ease;
        border: 1px solid transparent;
        font-family: inherit;
        background: #fff;
    }

    .ssm-btn-sm {
        padding: 5px 10px;
        font-size: 12px;
    }

    .ssm-btn-primary {
        background: @primary;
        color: #fff;
        border-color: @primary;

        &:hover {
            background: #337ECC;
        }
    }

    .ssm-btn-default {
        color: @text-regular;
        border-color: @border;

        &:hover {
            color: @primary;
            border-color: @primary;
            background: @primary-light;
        }
    }

    .ssm-btn-danger-outline {
        color: @danger;
        border-color: #FBC4C4;

        &:hover {
            background: @danger-bg;
            border-color: @danger;
        }
    }

    .ssm-filter-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: nowrap;
        padding: 12px 16px;
        margin: 0;
        // background: @bg;
        border-bottom: 1px solid @border-light;

        /deep/ .el-form-item {
            margin-bottom: 0;
            margin-right: 10px;
        }

        .ssm-filter-left {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            min-width: 0;
        }

        .ssm-filter-search {
            margin-right: 0;
            margin-left: auto;
        }
    }

    .ssm-panel-body {
        padding: 0;
        min-height: 200px;

        /deep/ .el-table {
            font-size: 13px;

            &::before {
                display: none;
            }
        }

        /deep/ .el-table__header th {
            background: @bg;
            color: @text-regular;
            font-weight: 600;
            font-size: 12px;
        }

        /deep/ .el-table__empty-block {
            min-height: 200px;
        }
    }

    .ssm-cell-title {
        font-weight: 600;
        color: @text-primary;
    }

    .ssm-cell-classes {
        line-height: 1.55;
    }

    .ssm-class-line {
        font-size: 13px;
        color: @text-regular;
        line-height: 1.55;

        &+.ssm-class-line {
            margin-top: 2px;
        }
    }

    .ssm-origin-tag {
        display: inline-flex;
        align-items: center;
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;

        &.project {
            background: #EEF2FF;
            color: #4338CA;
        }

        &.school {
            background: #FFF7ED;
            color: #C2410C;
        }
    }

    .ssm-delete-btn {
        color: @danger;

        &:hover,
        &:focus {
            color: #f78989;
        }
    }

    .ssm-pagination {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-top: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 8px;
    }

    .ssm-pagination-info {
        font-size: 13px;
        color: @text-secondary;
    }

    .ssm-required {
        color: @danger;
    }

    .ssm-form-hint {
        font-size: 12px;
        color: @text-secondary;
        margin: 0 0 8px;
        line-height: 1.5;
    }

    .ssm-check-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        max-height: 180px;
        overflow-y: auto;
        padding: 4px;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: @bg;
    }

    .ssm-check-item {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        padding: 8px 10px;
        background: #fff;
        border-radius: 6px;
        border: 1px solid @border-light;
        font-size: 12px;
        cursor: pointer;
        margin: 0;

        &:hover {
            border-color: @primary;
        }

        /deep/ .el-checkbox {
            display: flex;
            align-items: flex-start;
            white-space: normal;
            line-height: 1.4;
        }

        /deep/ .el-checkbox__label {
            white-space: normal;
            line-height: 1.4;
            font-size: 12px;
            color: @text-regular;
        }
    }

    .ssm-import-steps {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .ssm-import-step {
        padding: 14px 16px;
        border: 1px solid @border-light;
        border-radius: 8px;
        background: @bg;
    }

    .ssm-import-step-title {
        font-size: 13px;
        font-weight: 700;
        color: @text-primary;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .ssm-import-step-num {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: @primary;
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .ssm-import-desc {
        font-size: 12px;
        color: @text-secondary;
        margin: 0 0 10px;
        line-height: 1.6;
    }

    .ssm-import-tpl-fields {
        font-size: 12px;
        color: @text-regular;
        line-height: 1.7;
        margin: 0;
        padding-left: 18px;
    }

    .ssm-upload-zone {
        border: 1.5px dashed @border;
        border-radius: 8px;
        background: #fff;
        padding: 28px 16px;
        text-align: center;
        cursor: pointer;
        transition: all 0.25s ease;

        &:hover,
        &.dragover {
            border-color: @primary;
            background: @primary-light;
        }
    }

    .ssm-uz-icon {
        font-size: 28px;
        margin-bottom: 8px;
    }

    .ssm-uz-title {
        font-size: 13px;
        font-weight: 600;
        color: @text-primary;
    }

    .ssm-uz-hint {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 4px;
    }

    .ssm-uz-file {
        font-size: 12px;
        color: @primary;
        margin-top: 8px;
        font-weight: 500;
        word-break: break-all;
    }

    .ssm-import-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 10px;
        font-size: 12px;
    }

    .ssm-sum-item {
        padding: 4px 10px;
        border-radius: 100px;
        background: #fff;
        border: 1px solid @border-light;
        color: @text-regular;

        &.ok {
            background: @success-bg;
            color: @success;
            border-color: transparent;
        }

        &.err {
            background: @danger-bg;
            color: @danger;
            border-color: transparent;
        }
    }

    .ssm-import-preview-wrap {
        max-height: 220px;
        overflow: auto;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: #fff;
    }

    .ssm-import-empty {
        padding: 20px;
        text-align: center;
        color: @text-secondary;
        font-size: 12px;
    }

    .ssm-import-preview {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;

        th {
            position: sticky;
            top: 0;
            background: @bg;
            text-align: left;
            padding: 8px 10px;
            font-weight: 600;
            color: @text-regular;
            border-bottom: 1px solid @border-light;
        }

        td {
            padding: 8px 10px;
            border-bottom: 1px solid @border-light;
            vertical-align: top;
            color: @text-regular;
        }

        tr.is-invalid td {
            background: #FFF5F5;
        }

        .row-ok {
            color: @success;
            font-weight: 600;
        }

        .row-err {
            color: @danger;
            font-weight: 600;
        }
    }

    @media (max-width: 768px) {
        .ssm-filter-bar {
            flex-wrap: wrap;

            .ssm-filter-left {
                flex-wrap: wrap;
            }

            .ssm-filter-search {
                margin-left: 0;
                width: 100%;
            }
        }

        .ssm-check-grid {
            grid-template-columns: 1fr;
        }
    }
</style>

<style lang="less">

    .ssm-form-dialog,
    .ssm-import-dialog {
        .el-dialog__body {
            padding-top: 10px;
        }

        .el-form-item__label {
            font-size: 13px;
            font-weight: 600;
            color: #303133;
            padding-bottom: 0;
            line-height: 1.4;
        }

        .el-form-item {
            margin-bottom: 16px;
        }
    }
</style>
